import React from "react";

import { IS_APP } from "@/__main__/constants.mjs";
import { appURLs } from "@/app/constants.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import { showAugmentStats } from "@/game-tft/constants.mjs";
import { GAME_SYMBOL_TFT } from "@/game-tft/definition-symbol.mjs";
import routes from "@/game-tft/routes.mjs";
import type { GameOverlayFeature } from "@/hub-overlays/constants.mjs";
import UpdateIcon from "@/inline-assets/card-stack.svg";
import GameIconBoxTFT from "@/inline-assets/game-icon-box-tft.svg";
import FriendGameIconBoxTFT from "@/inline-assets/game-icon-friend-box-tft.svg";
import GameShapeTFT from "@/inline-assets/GameShapeTFT.svg";
import IconNavProBuilds from "@/inline-assets/nav-pro-builds.svg";
import IconTFTComps from "@/inline-assets/tft-comps.svg";

export const TFT_OVERLAY_SETTINGS = {
  isChampionsOverlayEnabled: true,
  isLevelingOverlayEnabled: true,
  isTFTCompsOverlayEnabled: true,
  isTFTItemsOverlayEnabled: true,
  isTFTNextOpponentEnabled: true,
  isTFTOpponentTraitsEnabled: true,
  isTFTBenchmarkingOverlayEnabled: true,
  isStreamInfoOverlayEnabled: false,
  isOpponentTagsEnabled: false,
  isTFTOverlayEnabled: true,
  isTFTSkinsEnabled: true,
  compositionsOverlay: {
    compactMode: true,
    skinArtwork: true,
    size: {
      min: 0,
      max: 200,
      value: 100,
    },
    opacity: {
      min: 10,
      max: 100,
      value: 25,
      step: 5,
    },
    position: {
      x: 0,
      y: 0,
    },
  },
  compositionsModalOverlay: {
    position: {
      x: 0,
      y: 0,
    },
  },
  benchmarkingOverlay: {
    size: {
      min: 0,
      max: 200,
      value: 100,
    },
    opacity: {
      min: 10,
      max: 100,
      value: 20,
    },
    position: {
      x: 0,
      y: 0,
    },
    roundsWon: false,
    level: true,
    boardValue: true,
    avgUnitStars: false,
    interestEarned: true,
    rerolls: false,
    streakIncome: false,
  },
  streamInfoOverlay: {
    size: {
      min: 0,
      max: 200,
      value: 100,
    },
    opacity: {
      min: 0,
      max: 100,
      value: 100,
    },
    smallRankIcon: false,
    placements: true,
    avgPlace: true,
    gamesFromHours: {
      min: 0,
      max: 120,
      value: 12,
      step: 1,
    },
    queue: 1100,
    position: {
      x: 620,
      y: 0,
    },
  },
  itemCombinationsOverlay: {
    size: {
      min: 0,
      max: 200,
      value: 100,
    },
    position: {
      x: 0,
      y: 0,
    },
  },
  championInfoOverlay: {
    skinArtwork: true,
  },
  isTFTAugmentOverlayEnabled: true,
};

export { GAME_SYMBOL_TFT };
const onToggle = async (overlay, enabled) =>
  (await import("@/game-tft/OverlayAppSettings.jsx")).onToggle(
    overlay,
    enabled,
  );

const definition = {
  gameSymbol: GAME_SYMBOL_TFT,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/TFT3-small.svg`,
  iconShape: GameShapeTFT,
  iconBox: GameIconBoxTFT,
  iconBadge: FriendGameIconBoxTFT,
  imageHero: `${appURLs.CDN_PLAIN}/blitz/games/assest/hero-logo/hero-logo-tft.png`,
  imageBanner: `${appURLs.CDN}/blitz/library/banner-tft.webp`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/tft-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/gamecover/name-tft.webp`,
  isActive: true,
  fullName: ["common:games.tft.long", "Teamfight Tactics"],
  friendlyName: ["common:games.tft.short", "TFT"],
  preferFriendlyName: false as const,
  shortName: "tft" as const,
  backendName: "TFTV2" as const,
  gameColor: "#F59511",

  settings: () => import("@/game-tft/Settings.jsx"),
  routes,

  IPC: () => import("@/game-tft/init.mjs"),

  overlays: {
    compositions: {
      key: ["isTFTCompsOverlayEnabled"],
      title: ["overlays:tft.compositions.title", "Compositions"],
      tags: ["utility"],
      description: [
        "overlays:tft.compositions.description",
        "View an up-to-date breakdown of the strongest unit comps and ideal items for those units.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-comps-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-comps-sm.webp`,
        featured: `${appURLs.CDN}/blitz/overlay-marketplace/compositions-featured.webp`,
      },
      native: true,
      date: new Date(500),
      features: IS_APP
        ? async () =>
            (await import("@/game-tft/OverlayAppSettings.jsx"))
              .comps as GameOverlayFeature<typeof TFT_OVERLAY_SETTINGS>[]
        : undefined,
      onToggle: onToggle,
    },
    matchTracking: {
      key: ["isTFTNextOpponentEnabled"],
      title: ["overlays:tft.matchupTracking.title", "Matchup Tracking"],
      tags: ["tracker"],
      description: [
        "overlays:tft.matchupTracking.description",
        "Keep track of opponents you've faced previously.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/matchup-tracking-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-matchupTrackingOverlay.webp`,
      },
      native: true,
      date: new Date(776),
      onToggle: onToggle,
    },
    benchmarking: {
      key: ["isTFTBenchmarkingOverlayEnabled"],
      title: ["overlays:tft.benchmarking.title", "Benchmarking"],
      tags: ["coaching", "tracker"],
      description: [
        "overlays:tft.benchmarking.description",
        "Track key performance metrics to better gauge performance in real time.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/benchmarking-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-benchmarking-sm.webp`,
      },
      native: true,
      date: new Date(500),
      features: IS_APP
        ? async () =>
            (await import("@/game-tft/OverlayAppSettings.jsx"))
              .benchmarking as GameOverlayFeature<typeof TFT_OVERLAY_SETTINGS>[]
        : undefined,
      onToggle: onToggle,
    },
    // augmentInfo: {
    //   key: ["isTFTAugmentOverlayEnabled"],
    //   title: ["overlays:tft.augmentInfo.title", "Augment Info"],
    //   tags: ["utility"],
    //   description: [
    //     "overlays:tft.augmentInfo.description",
    //     "View live statistics on Augments offered in the game.",
    //   ],
    //   images: {
    //     large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/augment-info-portrait.webp`,
    //     small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-augmentinfo-sm.webp`,
    //   },
    //   native: true,
    //   date: new Date(500),
    //   onToggle: onToggle,
    // },
    championInfo: {
      key: ["isChampionsOverlayEnabled"],
      title: ["overlays:tft.championInfo.title", "Champion Info"],
      tags: ["utility"],
      description: [
        "overlays:tft.championInfo.description",
        "Discover the best items for any Champion and the probability of their appearance.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/champion-info-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-championsOverlay.webp`,
      },
      native: true,
      date: new Date(500),
      features: IS_APP
        ? async () =>
            (await import("@/game-tft/OverlayAppSettings.jsx"))
              .championInfo as GameOverlayFeature<typeof TFT_OVERLAY_SETTINGS>[]
        : undefined,
      onToggle: onToggle,
    },
    itemCombinations: {
      key: ["isTFTItemsOverlayEnabled"],
      title: ["overlays:tft.itemCombinations.title", "Item Combinations"],
      tags: ["utility"],
      description: [
        "overlays:tft.itemCombinations.description",
        "View all the item combinations you can build to power up your units.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/item-combinations-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-itemsOverlay.webp`,
      },
      native: true,
      date: new Date(500),
      features: IS_APP
        ? async () =>
            (await import("@/game-tft/OverlayAppSettings.jsx"))
              .itemCombinations as GameOverlayFeature<
              typeof TFT_OVERLAY_SETTINGS
            >[]
        : undefined,
      onToggle: onToggle,
    },
    levelingInfo: {
      key: ["isLevelingOverlayEnabled"],
      title: ["overlays:tft.levelingInfo.title", "Leveling Info"],
      tags: ["coaching", "utility"],
      description: [
        "overlays:tft.levelingInfo.description",
        "Monitor your XP and find out when it's a good time to level up.",
      ],
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/leveling-info-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-levelingOverlay.webp`,
      },
      native: true,
      date: new Date(500),
      onToggle: onToggle,
    },
    // opponentTags: {
    //   key: ["isOpponentTagsEnabled"],
    //   title: ["overlays:tft.opponentTags.title", "Opponent Tags"],
    //   tags: ["utility"],
    //   description: [
    //     "overlays:tft.opponentTags.description",
    //     "Keep track of opponent compositions by adding tags to their portrait.",
    //   ],
    //   images: {
    //     large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/opponent-tags-portrait.webp`,
    //     small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-opponentTagsOverlay.webp`,
    //   },
    //   native: true,
    //   date: new Date(500),
    // },
    streamInfo: {
      key: ["isStreamInfoOverlayEnabled"],
      title: ["overlays:tft.streamInfo.title", "Stream Info"],
      tags: ["utility"],
      description: [
        "overlays:tft.streamInfo.description",
        "Share your username, rank and stats with your stream.",
      ],
      noToggle: true,
      images: {
        large: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/stream-info-portrait.webp`,
        small: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-streaminfo-sm.webp`,
      },
      date: new Date(500),
      Preview: React.lazy(
        () => import("@/game-tft/InGameOverlayStreamInfoExternal.jsx"),
      ),
      features: async () =>
        (await import("@/game-tft/InGameOverlayStreamInfoSettings.jsx"))
          .default as GameOverlayFeature<typeof TFT_OVERLAY_SETTINGS>[],
      onToggle: onToggle,
    },
  },

  streamerOverlays: {},

  overlaySettingsKey: "tftOverlaySettings",
  overlaySettings: TFT_OVERLAY_SETTINGS,

  meta: {
    game: {
      title: [
        "home:meta.home.tft.title",
        "Blitz, the best tracker for players to win TFT",
      ],
      description: [
        "home:meta.home.tft.description",
        "Expert recommendations, data-driven match analysis, pro builds, guides, overlays, and competitions — personalized for gamers of any skill level.",
      ],
    },
    overlays: {
      title: [
        "overlays:meta.tft.title",
        "Teamfight Tactics Overlays, Tools, TFT In Game Coaching",
      ],

      description: [
        "overlays:meta.tft.description",
        "TFT comps in game, matchup tracking, traits tracking, item cheat sheet, leveling tips and coaching overlays",
      ],

      subtitle: [
        "overlays:meta.tft.subtitle",
        "Get all the information you need to dominate live with TFT overlays powered by Blitz.",
      ],
    },
    pros: [
      {
        name: "Keane",
        src: "keane-8eb3d4.webp",

        region: "na1",
        id: "enaek",
      },
      {
        name: "Kiyoon",
        src: "kiyoon-31e91a.webp",

        region: "na1",
        id: "tsm kiyoon",
      },
      {
        name: "Souless",
        src: "souless-31e91a.webp",

        region: "na1",
        id: "tsm souless",
      },
      {
        name: "Emilyywang",
        src: "emilyywang-b29eaa.webp",

        region: "na1",
        id: "emilyywang",
      },
    ],
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/tft-30c81.webp`,
    bgVideo: `${appURLs.CDN_VIDEOS}/homepage/tft-homepage-bg-video.webm`,
    players: "50M+",
    matches: "550M+",
    guides: [
      () => import("@/game-tft/HomeCompsTile.jsx"),
      () => import("@/game-tft/HomeChampionsTile.jsx"),
    ],
    features: [
      {
        title: ["tft.features.composition-stats.title", "Composition Stats"],
        description: [
          "tft.features.composition-stats.description",
          "View top comps in the meta",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/composition-stats.webp`,
      },
      {
        title: ["tft.features.item-stats.title", "Item Stats"],
        description: [
          "tft.features.item-stats.description",
          "View top items in the meta",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/item-stats.webp`,
      },
      {
        title: ["tft.features.champion-stats.title", "Champion Stats"],
        description: [
          "tft.features.champion-stats.description",
          "View top champions in the meta",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/champion-stats.webp`,
      },
      {
        title: ["tft.features.trait-stats.title", "Trait Stats"],
        description: [
          "tft.features.trait-stats.description",
          "View top traits in the meta",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/trait-stats.webp`,
      },
      {
        title: [
          "tft.features.benchmarking-overlay.title",
          "Benchmarking Overlay",
        ],
        description: [
          "tft.features.benchmarking-overlay.description",
          "Track key performance metrics to better gauge performance in real time",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/benchmarking-overlay.webp`,
      },
      {
        title: [
          "tft.features.composition-overlay.title",
          "Composition Overlay",
        ],
        description: [
          "tft.features.composition-overlay.description",
          "View an up-to-date breakdown of the strongest unit comps and ideal items for those units",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/composition-overlay.webp`,
      },
      {
        title: [
          "tft.features.item-combinations-overlay.title",
          "Item Combinations Overlay",
        ],
        description: [
          "tft.features.item-combinations-overlay.description",
          "View all the item combinations you can build to power up your units",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/item-combinations-overlay.webp`,
      },
      {
        title: [
          "tft.features.leveling-info-overlay.title",
          "Leveling Info Overlay",
        ],
        description: [
          "tft.features.leveling-info-overlay.description",
          "Monitor your XP and find out when it's a good time to level up",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/leveling-info-overlay.webp`,
      },
      {
        title: [
          "tft.features.matchup-tracking-overlay.title",
          "Matchup Tracking Overlay",
        ],
        description: [
          "tft.features.matchup-tracking-overlay.description",
          "Keep track of opponents you've faced previously",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/matchup-tracking-overlay.webp`,
      },
      {
        title: [
          "tft.features.champion-info-overlay.title",
          "Champion Info Overlay",
        ],
        description: [
          "tft.features.champion-info-overlay.description",
          "Discover the best items for any Champion and the probability of their appearance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/champion-info-overlay.webp`,
      },
      {
        title: [
          "tft.features.stream-info-overlay.title",
          "Stream Info Overlay",
        ],
        description: [
          "tft.features.stream-info-overlay.description",
          "Share your username, rank and stats with your stream",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/stream-info-overlay.webp`,
      },
      {
        title: ["tft.features.pro-builds.title", "Pro Builds"],
        description: [
          "tft.features.pro-builds.description",
          "Recent compositions used by Pros",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/tft/pro-builds.webp`,
      },
    ],
    articles: [
      {
        createdAt: new Date("2025-07-13"),
        title: ["home:articles.tft.set15.title", "Set 15 Reveal"],
        subtitle: [
          "home:articles.tft.set15.subtitle",
          "Set 15 is revealed. Check out our Set Update & comps page.",
        ],
        background: `${appURLs.CDN}/homepage/news/tft-set45-revival.webp`,
        link: {
          href: "/tft/set-15",
        },
        links: [
          {
            Icon: UpdateIcon,
            href: "/tft/set-15",
            text: ["common:navigation.set15", "Set 15 Reveal"],
          },
          {
            Icon: IconTFTComps,
            href: "/tft/comps?set=15",
            text: ["common:navigation.comps", "Comps"],
          },
          {
            Icon: IconNavProBuilds,
            href: "/tft/probuilds",
            text: ["common:navigation.proBuilds", "Pro Builds"],
          },
        ],
      },
      {
        createdAt: new Date("2023-10-06"),
        title: ["home:articles.tft.early-comps.title", "Early Comps"],
        subtitle: [
          "home:articles.tft.early-comps.subtitle",
          "Discover the best TFT early game comps and opener builds in the meta.",
        ],
        link: {
          href: "/tft/comps/early",
          text: ["tft:viewComps", "View Comps"],
        },
        background: `${appURLs.CDN}/homepage/news/tft-earlycomps.webp`,
      },
      {
        createdAt: new Date("2022-11-10"),
        title: [
          "home:articles.lol.benchmarking-overlay.titley",
          "Benchmarking Overlay",
        ],
        subtitle: [
          "home:articles.lol.benchmarking-overlay.subtitle",
          "Track key performance metrics to better gauge performance in real time",
        ],
        link: {
          href: "/overlays/tft",
          text: ["common:overlay.view", "View Overlay"],
        },
        background: `${appURLs.CDN}/homepage/news/tft-benchmarking-hd.webp`,
      },
    ],
    homeData: () => import("@/game-tft/tft-fetch-homepage.mjs"),
  },

  navLinks: [
    {
      href: "/tft/comps",
      text: ["common:navigation.comps", "Comps"],
      menu: [
        [
          {
            items: [
              {
                href: "/tft/comps?set=7_5",
                text: ["tft:common.set75Comps", "Set 7.5 Revival Comps"],
              },
              {
                href: "/tft/comps?set=15",
                text: ["tft:common.set15Comps", "Set 15 Comps"],
              },
              {
                href: "/tft/comps/early",
                text: ["tft:common.earlyComps", "Early Comps"],
              },
            ],
          },
        ],
      ],
    },
    {
      href: "/tft/set-15",
      text: ["tft:set15.title", "Set 15"],
      tag: ["common:new", "New"],
      menu: [
        [
          {
            items: [
              {
                href: "/tft/comps?set=15",
                text: ["tft:common.set15Comps", "Set 15 Comps"],
              },
              {
                href: "/tft/set-15/guides",
                text: ["tft:set15.guides", "Set 15 Guides"],
              },
              {
                href: "/tft/set-15/powerups",
                text: ["tft:set15.powerups", "Powerups"],
              },
              {
                href: "/tft/set-15/crystal-gambit",
                text: ["tft:set15.crystalGambit", "Crystal Gambit Loot"],
              },
              {
                href: "/tft/set-15",
                text: ["home:articles.tft.set15.title", "Set 15 Reveal"],
              },
            ],
          },
        ],
      ],
    },
    {
      href: "/tft/champions",
      text: ["common:navigation.statistics", "Statistics"],
      menu: [
        [
          {
            items: [
              {
                href: "/tft/champions/stats",
                text: ["lol:champion_plural", "Champions"],
              },
              {
                href: "/tft/traits/stats",
                text: ["tft:common.traits", "Traits"],
              },
              {
                href: "/tft/items/stats",
                text: ["common:navigation.items", "Items"],
              },
              showAugmentStats
                ? {
                    href: "/tft/augments/stats",
                    text: ["common:navigation.augments", "Augments"],
                  }
                : undefined,
            ],
          },
        ],
      ],
    },
    {
      href: "/tft/tierlist",
      text: ["common:navigation.tierlist", "Tier List"],
      menu: [
        [
          {
            items: [
              {
                href: "/tft/tierlist",
                text: ["lol:champion_plural", "Champions"],
              },
              {
                href: "/tft/tierlist/items",
                text: ["common:navigation.items", "Items"],
              },
              showAugmentStats
                ? {
                    href: "/tft/tierlist/augments",
                    text: ["common:navigation.augments", "Augments"],
                  }
                : undefined,
            ],
          },
        ],
      ],
    },
    {
      href: "/tft/set-15/guides",
      text: ["common:navigation.guides", "Guides"],
      menu: [
        [
          {
            items: [
              {
                href: "/tft/set-15/guides",
                text: ["tft:set15.guides", "Set 15 Guides"],
              },
              {
                href: "/tft/guides/reroll",
                text: ["tft:guides.re-roll", "Re-Roll"],
              },
              // {
              //   href: "/tft/guides/loadeddice",
              //   text: ["tft:loadedDice", "Loaded Dice"],
              // },
              {
                href: "/tft/guides/rounds",
                text: ["tft:rounds", "Rounds"],
              },
              {
                href: "/tft/guides/gold",
                text: ["tft:guides.gold-xp", "Gold / XP"],
              },
              {
                href: "/tft/guides/damage",
                text: ["tft:guides.damage-formula", "Damage Formula"],
              },
              // {
              //   href: "/tft/guides/tome-of-traits",
              //   text: ["tft:guides.tome-of-traits", "Tome of Traits"],
              // },
            ],
          },
        ],
      ],
    },
    {
      href: "/overlays/tft",
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
    {
      href: "/tft/probuilds",
      text: ["common:navigation.proBuilds", "Pro Builds"],
    },
  ],

  homepageFeatures: [
    "blitz-search",
    {
      type: "contentful-articles",
      title: ["common:news", "News"],
      description: [
        "common:news.descriptionGame",
        "What’s new with {{gameName}}",
        {
          gameName: "Teamfight Tactics",
        },
      ],
    },
    "blitz-guides",
    {
      type: "contentful-grid",
      title: ["tft:contentfulgrid.title", "Latest Guides"],
      description: [
        "tft:contentfulgrid.description",
        "Stay up to date with the latest guides!",
      ],
    },
    "blitz-articles",
    "blitz-game-features",
  ],
} satisfies GameDefinition<typeof GAME_SYMBOL_TFT>;

export default definition;
